import { AnimatePresence, motion } from "motion/react";
import React, {
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { SearchIcon } from "@/shared/icons";
import { Switcher } from "@/shared/ui";
import { ChatState } from "@/domains/chat/slice";

type Props = {
  onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
  message: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLImageElement>) => void;
  onChangeCommand: (value: string) => void;
  isEnabled: boolean;
  command: string;
  showTooltip?: boolean;
  tooltipCommands?: ChatState["tooltipCommands"];
  tooltipCommandsAll?: ChatState["tooltipCommands"];
  tooltipCommandsPosition?: number;
  isBMode?: boolean;
  onChangeBMode: () => void;
  show?: boolean;
  onToogleSearch: () => void;
};

const animations = {
  show: {
    opacity: 1,
  },
  hide: {
    opacity: 0,
  },
};

function ChatInput({
  message,
  onSubmit,
  onChange,
  isEnabled,
  onFocus,
  onBlur,
  onKeyDown,
  showTooltip,
  command,
  tooltipCommands = [],
  tooltipCommandsAll = [],
  tooltipCommandsPosition = -1,
  onChangeCommand,
  isBMode,
  onChangeBMode,
  show = true,
  onToogleSearch,
}: Props) {
  const { t } = useTranslation("chat");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEnabled) {
      inputRef.current.focus();
    } else {
      inputRef.current.blur();
    }
  }, [isEnabled]);

  const showCommands = showTooltip && !!tooltipCommands.length;

  const submitHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onSubmit();
    },
    [onSubmit],
  );

  return (
    <motion.form
      initial={{ opacity: 0 }}
      variants={animations}
      animate={show ? "show" : "hidden"}
      className={clsx(
        "relative h-[2.625rem] shrink-0 z-1 w-full flex items-center gap-2",
        "bg-gradient-to-r from-dark-900/80 to-dark-900/60",
        showCommands ? "rounded-t-[0.5rem]" : "rounded-[0.5rem]",
      )}
      onSubmit={submitHandler}
    >
      <div className="h-5 flex items-center gap-2.5 px-4 w-full">
        {command && (
          <div
            className={clsx(
              "h-full flex px-2 items-center justify-center uppercase",
              "font-extrabold text-12 text-white rounded-[0.25rem] bg-gradient-red",
            )}
          >
            {command}
          </div>
        )}

        <input
          ref={inputRef}
          className="bg-transparent grow font-medium text-15 text-white/70"
          onInput={onChange}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          value={message}
          maxLength={10000}
          placeholder={t("chat.enterMessage")}
          spellCheck="false"
        />
        <div className="flex items-center gap-1.5 ml-auto">
          <button type="button" onClick={onToogleSearch} className="group">
            <SearchIcon size={16} className="text-white/60 group-hover:text-white transition" />
          </button>
          <div className="w-0.5 h-3 bg-white/10 mx-1" />
          <div
            className={clsx(
              "flex items-center justify-center h-4 aspect-square rounded-[0.25rem] bg-white",
              "text-11 text-dark-800 font-bold leading-[0.75]",
            )}
          >
            B
          </div>
          <Switcher on={isBMode} onChange={onChangeBMode} />
        </div>
      </div>

      <AnimatePresence>
        {showCommands ? (
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{
              duration: 0.2,
            }}
            exit={{
              opacity: 0,
            }}
            className={clsx(
              "absolute top-[100%] flex flex-col gap-1 w-full pb-2 rounded-b-[0.5rem]",
              "bg-gradient-to-r from-dark-900/80 to-dark-900/60",
            )}
            key="chat-input"
          >
            <div className="px-4 flex items-center gap-2 py-1">
              <div className="text-12 font-medium text-white leading-none">
                Подсказки к командам
              </div>
              <div className="h-[1px] bg-white/30 grow" />
            </div>
            <div className="flex flex-col overflow-x-clip">
              {tooltipCommandsAll.map((item, index) => (
                <AnimatePresence key={item.value}>
                  {tooltipCommands.includes(item) ? (
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "1.5rem" }}
                      transition={{
                        type: "spring",
                        duration: 0.2,
                      }}
                      exit={{ opacity: 0, height: 0 }}
                      className={clsx(
                        "flex items-center justify-between h-[1.5rem] text-13 font-medium text-accent leading-none",
                        "cursor-pointer w-max min-w-[100%] gap-3 px-4 hover:bg-accent-solid/20",
                        { "bg-accent-solid/30": tooltipCommandsPosition === index },
                      )}
                      onClick={() => onChangeCommand(item.value)}
                    >
                      <div className="flex items-center gap-3">
                        <span>/{item.value}</span>
                        {item?.args &&
                          item.args.map((arg) => (
                            <span key={arg} className="text-white">
                              [{arg}]
                            </span>
                          ))}
                      </div>
                      {item?.description && (
                        <span className="text-white/60">{item.description}</span>
                      )}
                    </motion.button>
                  ) : null}
                </AnimatePresence>
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.form>
  );
}

export default React.memo(ChatInput);
